import React, { useState, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import { FaCheckCircle } from 'react-icons/fa';
import './PathComponent.css';

function parsePathColorHex(raw) {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(s)) return null;
  if (s.length === 4) {
    return `#${s[1]}${s[1]}${s[2]}${s[2]}${s[3]}${s[3]}`.toLowerCase();
  }
  return s.toLowerCase();
}

function contrastTextForHexBg(hex) {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 156 ? '#212529' : '#ffffff';
}

/**
 * PathComponent - Displays a visual path/stage progression for picklist fields
 * Similar to Salesforce Path component
 *
 * @param {Object} field - The field configuration with is_path_field = true
 * @param {String} currentValue - The current value of the picklist field
 * @param {Function} onStageChange - Callback when user changes the stage
 * @param {Array} picklistValues - Array of picklist values/stages
 * @param {Boolean} isUpdating - Whether an update is in progress
 */
const PathComponent = ({ field, currentValue, onStageChange, picklistValues = [], isUpdating = false }) => {
  const [selectedStage, setSelectedStage] = useState(null);
  const [currentStageIndex, setCurrentStageIndex] = useState(-1);

  useEffect(() => {
    // Find the index of the current value
    const currentIndex = picklistValues.findIndex(
      pv => pv.key === currentValue || pv.value === currentValue
    );
    setCurrentStageIndex(currentIndex);
  }, [currentValue, picklistValues]);

  const isStageSelectable = stage =>
    stage && stage.role_selectable !== false;

  const handleStageClick = (stage, index) => {
    // Don't allow selection during update
    if (isUpdating) {
      return;
    }
    if (!isStageSelectable(stage)) {
      return;
    }
    setSelectedStage(stage);
  };

  const handleMarkAsCurrent = () => {
    if (
      selectedStage &&
      isStageSelectable(selectedStage) &&
      onStageChange &&
      !isUpdating
    ) {
      onStageChange(selectedStage.key || selectedStage.value);
      setSelectedStage(null);
    }
  };

  // Reset selected stage when update completes
  useEffect(() => {
    if (!isUpdating) {
      setSelectedStage(null);
    }
  }, [isUpdating]);

  if (!picklistValues || picklistValues.length === 0) {
    return null;
  }

  return (
    <div className={`path-component-container ${isUpdating ? 'updating' : ''}`}>
      <div className="path-component">
        <div className="path-stages">
          {picklistValues.map((stage, index) => {
            const isActive = index === currentStageIndex;
            const isSelected = selectedStage?.key === stage.key || selectedStage?.value === stage.value;
            const isCompleted = index < currentStageIndex;
            const isFuture = index > currentStageIndex;
            const selectable = isStageSelectable(stage);
            const cursor =
              isUpdating || !selectable ? 'not-allowed' : 'pointer';
            const customHex = selectable
              ? parsePathColorHex(stage.path_color || stage.pathColor)
              : null;
            const customFg = customHex ? contrastTextForHexBg(customHex) : null;
            /** Stages already passed or current use their picklist path_color; future stages stay default until clicked */
            const isPassed = isCompleted || isActive;
            const applyCustomPathColor =
              Boolean(customHex) &&
              selectable &&
              (isPassed || isSelected);

            return (
              <div
                key={stage.key || stage.value || index}
                className={`path-stage ${isActive ? 'active' : ''} ${isSelected ? 'selected' : ''} ${isCompleted ? 'completed' : ''} ${isFuture ? 'future' : ''} ${!selectable ? 'path-stage-not-selectable' : ''} ${applyCustomPathColor ? 'path-stage-custom-color' : ''}`}
                onClick={() => handleStageClick(stage, index)}
                title={`${stage.label || stage.value}${isActive ? ' (Current)' : ''}${isCompleted ? ' (Completed)' : ''}${isSelected ? ' (Selected - Click button to update)' : ''}${!selectable ? ' (Not available for your role)' : ''}`}
                style={{
                  cursor,
                  pointerEvents: isUpdating ? 'none' : 'auto',
                  ...(applyCustomPathColor
                    ? {
                      '--path-custom-bg': customHex,
                      '--path-custom-fg': customFg,
                    }
                    : {}),
                }}
              >
                <div className="stage-content">

                  <span className="stage-label mx-2">
                    {stage.label || stage.value}
                  </span>
                  {isCompleted && !isSelected && !isActive && (
                    <FaCheckCircle className="stage-icon completed-icon" style={{ marginRight: '6px', fontSize: '12px' }} />
                  )}
                </div>
                {index < picklistValues.length - 1 && (
                  <div className="stage-arrow"></div>
                )}
              </div>
            );
          })}
        </div>

        {selectedStage && !isUpdating && (
          <div className="path-actions">
            <Button
              variant="primary"
              size="sm"
              onClick={handleMarkAsCurrent}
              disabled={isUpdating}
              className="mark-stage-btn"
            >
              Mark as Current Stage
            </Button>
          </div>
        )}

        {isUpdating && (
          <div className="path-actions">
            <Button
              variant="primary"
              size="sm"
              disabled={true}
              className="mark-stage-btn"
            >
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Updating...
            </Button>
          </div>
        )}
      </div>

      {/*field?.field_label && (
        <div className="path-field-info">
          <small className="text-muted">
            Path: {field.field_label}
          </small>
        </div>
      )*/}
    </div>
  );
};

export default PathComponent;
